import { useMemo, useState } from "react";

import { Button } from "atoms/button/Button";
import { TextButton } from "atoms/text-button/TextButton";

import { useTranslation } from "@/core/i18n";

import { PersonalisationHeading } from "./PersonalisationHeading";
import { PersonalisationProgressBar } from "./PersonalisationProgressBar";
import { PersonalisationScreenLayout } from "./PersonalisationScreenLayout";
import { SingleChoiceTagPicker } from "./SingleChoiceTagPicker";

interface SkinSunItem {
  id: string;
  label: string;
}

interface SkinSunProps {
  options?: SkinSunItem[];
  onContinue: (selection: string) => void;
  onSkip?: () => void;
  selectedColor?: string;
  continueButtonText?: string;
  skipText?: string;
  progressPercentage: number;
  onBack?: () => void;
  onClose?: () => void;
  initialSelectedId?: string | null;
}

export const SkinSun = ({
  options,
  onContinue,
  onSkip,
  selectedColor,
  continueButtonText,
  skipText,
  progressPercentage,
  onBack,
  onClose,
  initialSelectedId,
}: SkinSunProps) => {
  const { t } = useTranslation();
  const defaultSkinSunOptions = useMemo<SkinSunItem[]>(
    () => [
      { id: "highly-sensitive", label: t("skinSun.optionHighlySensitive") },
      { id: "often-sensitive", label: t("skinSun.optionOftenSensitive") },
      { id: "usually-tan", label: t("skinSun.optionUsuallyTan") },
      { id: "rarely-sensitive", label: t("skinSun.optionRarelySensitive") },
      { id: "resilient", label: t("skinSun.optionResilient") },
    ],
    [t],
  );
  const resolvedOptions = options ?? defaultSkinSunOptions;
  const resolvedContinueButtonText = continueButtonText ?? t("common.continue");
  const resolvedSkipText = skipText ?? t("skinSun.skipForNow");
  const [selected, setSelected] = useState<SkinSunItem | null>(
    initialSelectedId ? (resolvedOptions.find((o) => o.id === initialSelectedId) ?? null) : null,
  );

  const handleSelectionChange = (item: SkinSunItem | null) => {
    setSelected(item);
    onContinue?.(item?.id ?? "");
  };

  const handleContinue = () => {
    if (!selected) {
      return;
    }
    onContinue(selected.id);
  };

  const handleSkip = () => {
    onSkip?.();
  };

  const handleBack = () => {
    onBack?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  const isContinueDisabled = selected === null;

  return (
    <PersonalisationScreenLayout
      header={
        <PersonalisationProgressBar
          progressPercentage={progressPercentage}
          onBack={handleBack}
          onClose={handleClose}
        />
      }
      footer={
        <>
          <Button
            style="primary"
            size="large"
            fullWidth
            disabled={isContinueDisabled}
            onClick={handleContinue}
          >
            {resolvedContinueButtonText}
          </Button>

          <TextButton variant="secondary" onClick={handleSkip}>
            {resolvedSkipText}
          </TextButton>
        </>
      }
    >
      <PersonalisationHeading size="sm">{t("skinSun.heading")}</PersonalisationHeading>

      <SingleChoiceTagPicker
        items={resolvedOptions}
        selectedId={selected?.id ?? null}
        onSelectionChangeRequest={handleSelectionChange}
        selectedColor={selectedColor}
        ariaLabel={t("skinSun.ariaLabel")}
      />
    </PersonalisationScreenLayout>
  );
};
